import { useTranslation } from 'react-i18next';
import AnalyticsTable from '@customer/components/tables/analytics-table/AnalyticsTable';
import AnalyticsSubPage from '@customer/templates/health-sub-page/AnalyticsSubPage';
import { useCallback, useMemo, useState } from 'react';
import {
  AssetBasicModel,
  AssetListScenarios,
  HealthSelfTestAssetsFilters,
} from '@customer/store/apis/assetsApi';
import { GridControlled } from '@customer/components/tables/grid/GridControlled';
import useAnalyticsSubPage from '@customer/templates/health-sub-page/useAnalyticsSubPage';
import { AssetSidePanelTab } from '@customer/store/slices/globalSlice';
import ChartScenarios from '@customer/components/charts/chart-scenarios/ChartScenarios';
import { ChartColorScheme, ScenarioConfig } from '@customer/components/charts/config';
import { HealthScenarioQueryParams, StatsScenario } from '@customer/store/apis/scenariosApi';
import BarChartByDeviceType from '@customer/components/charts/by-device-type/BarChartByDeviceType';
import BarChartByFaultCategory from '@customer/components/charts/by-fault-category/BarChartByFaultCategory';
import BarChartByFaultTestName from '@customer/components/charts/by-fault-test-name/BarChartByFaultTestName';
import GlobalTimeRangeFilter from '@customer/components/inputs/global-time-range/GlobalTimeRangeFilter';
import SelfTestOverview from './components/overview/SelfTestOverview';
import {
  columnDefAssetGroup,
  columnDefAssetName,
  columnDefConfigGroupName,
  columnDefDeviceType,
  columnDefObjectType,
  columnDefSoftwareStatus,
  columnDefTotalFaults,
} from '@customer/components/tables/grid/columnDefs';
import { ColDef } from 'ag-grid-community';
import { FilterGroup } from '@customer/filters/types';
import BarChartByAssetGroup from '@customer/components/charts/by-asset-group/BarChartByAssetGroup';

const AssetGridSliceKey = 'AssetsHealthSelfTest';
const API_TABLE_LIMIT = 20;

export function SelfTest() {
  const { t } = useTranslation();

  const [columnDefs] = useState<ColDef<AssetBasicModel>[]>([
    { ...columnDefAssetName(t), maxWidth: 150 },
    { ...columnDefObjectType(t), maxWidth: 150 },
    { ...columnDefDeviceType(t), maxWidth: 110 },
    { ...columnDefAssetGroup(t) },
    { ...columnDefConfigGroupName(t), maxWidth: 250 },
    { ...columnDefTotalFaults(t), maxWidth: 150 },
    { ...columnDefSoftwareStatus(t), maxWidth: 150 },
  ]);

  const { assets, hasData, noDataDescription, countAll, countFilter, onRowClick } =
    useAnalyticsSubPage<HealthSelfTestAssetsFilters>({
      scenario: AssetListScenarios.HealthSelfTest,
      filterGroup: FilterGroup.HealthSelfTest,
      sidePanelTab: AssetSidePanelTab.SelfTestSummary,
    });

  const getDataSetLabel = useCallback(
    (label: string) => {
      return t('AssetsWithLevelFaults', { level: Number(label) });
    },
    [t],
  );

  const scenarioConfigs = useMemo<ScenarioConfig[]>(
    () => [
      {
        title: t('ByAssetGroup'),
        ChartComponent: BarChartByAssetGroup<HealthScenarioQueryParams>,
        scenario: StatsScenario.SelfTestByAssetGroup,
      },
      {
        title: t('ByFaultCategory'),
        ChartComponent: BarChartByFaultCategory<HealthScenarioQueryParams>,
        scenario: StatsScenario.SelfTestByFaultCategory,
      },
      {
        title: t('ByTestName'),
        ChartComponent: BarChartByFaultTestName<HealthScenarioQueryParams>,
        scenario: StatsScenario.SelfTestByTestName,
      },
      {
        title: t('ByDeviceType'),
        ChartComponent: BarChartByDeviceType<HealthScenarioQueryParams>,
        scenario: StatsScenario.SelfTestByDeviceType,
      },
    ],
    [t],
  );

  return (
    <AnalyticsSubPage
      countAll={countAll}
      countFilter={countFilter}
      filters={FilterGroup.HealthSelfTest}
      header={<GlobalTimeRangeFilter disabled={false} />}
      content={
        <>
          <SelfTestOverview />

          <ChartScenarios
            title={t('AssetsWithFaults')}
            scenarioConfigs={scenarioConfigs}
            filterGroup={FilterGroup.HealthSelfTest}
            colorScheme={ChartColorScheme.RedOrangeGrey}
            getDataSetLabel={getDataSetLabel}
            tooltipTransKey="AssetsWithCount"
            includeTimeRange
          />

          <AnalyticsTable
            title={t('Assets')}
            hasData={hasData}
            noDataDescription={noDataDescription}
            apiResultLimit={API_TABLE_LIMIT}
          >
            <GridControlled<AssetBasicModel>
              sliceKey={AssetGridSliceKey}
              rowData={assets}
              columnDefs={columnDefs}
              noRowsMessage={t('No assets')}
              onRowClick={onRowClick}
            />
          </AnalyticsTable>
        </>
      }
    />
  );
}

export default SelfTest;
